import { useInvestigationsCards } from '@/hooks/useInvestigationsCards'
import { useMedReqCards } from '@/hooks/useMedReqCards'
import { canUpdatePatient, isReadOnly } from '@/lib/permissions'
import { usePendingDuplicatesQuery, useRunDuplicateCheckMutation } from '@/services/deduplication'
import { useLinkedPatientsQuery } from '@/services/linked-patients'
import {
  type UpdatePatientBody,
  usePatientByHashQuery,
  useUpdatePatientDetails,
  useVerifyPatientId
} from '@/services/patients'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import { Alert, Box, Grid, Snackbar, Typography } from '@mui/material'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import NavMenu from '../../shared/NavMenu'
import InvestigationsList from '../../shared/InvestigationsList'
import LinkedPatientsSection from '../../shared/LinkedPatientsSection'
import PatientFilesSection from '../../shared/patient-files/PatientFilesSection'
import PreviousMedicationRequestsList from '../../shared/PreviousMedicationRequestsList'
import BackButton from '../../ui/BackButton'
import Header from './sections/header'
import MedicalHistory from './sections/medical-history'
import PatientInfo from './sections/patient-info'
import PotentialDuplicateCard from './sections/potential-duplicate-card'
import SafeguardingQuestions from './sections/safeguarding-questions'
import SCRData from './sections/scr-data'
import PatientTimeline from './sections/patient-timeline'

export default function PatientDetailPage() {
  const { hash } = useParams<{ hash: string }>()
  if (!hash) {
    throw new Error('Patient hash is required for patient display')
  }

  const navigate = useNavigate()
  const location = useLocation()
  const readOnly = isReadOnly()
  const [activeTab, setActiveTab] = useState<'overview' | 'timeline'>('overview')
  const [showSuccess, setShowSuccess] = useState(false)
  const [verifyIdFeedback, setVerifyIdFeedback] = useState<{
    open: boolean
    severity: 'success' | 'error'
    message: string
  }>({
    open: false,
    severity: 'success',
    message: ''
  })

  const { data: patient } = usePatientByHashQuery(hash)
  const { data: linkedPatients } = useLinkedPatientsQuery(patient?.linkedPatientHash, hash)
  const navItems = useMemo(
    () =>
      [
        { id: 'patient-details', title: 'Patient details' },
        linkedPatients?.length ? { id: 'linked-patients', title: 'Linked patients' } : null,
        { id: 'medical-history', title: 'Medical history' },
        { id: 'patient-files', title: 'Patient files' },
        { id: 'medication-requests', title: 'Medication Requests' },
        { id: 'investigations', title: 'Investigations' },
        { id: 'scr-data', title: 'SCR Data' },
        { id: 'safeguarding-questions', title: 'Safeguarding questions' }
      ].filter(Boolean) as { id: string; title: string }[],
    [linkedPatients]
  )
  const { medicationRequests, isLoading: isMedReqsLoading } = useMedReqCards({ patientHash: hash })
  const { investigations, isLoading: isInvestigationsLoading } = useInvestigationsCards({ patientHash: hash })
  const patientOrderNumbers = useMemo(
    () => [...new Set(medicationRequests.map(medReq => medReq.orderNumber).filter((n): n is string => !!n))],
    [medicationRequests]
  )
  const updateMutation = useUpdatePatientDetails(hash)
  const verifyIdMutation = useVerifyPatientId(hash)
  const duplicateCheckMutation = useRunDuplicateCheckMutation()
  // The route isn't keyed by hash, so a linked-patient click would otherwise inherit the previous patient's spinner.
  const isRunningDuplicateCheck = duplicateCheckMutation.isPending && duplicateCheckMutation.variables === hash
  const { data: pendingDuplicates } = usePendingDuplicatesQuery(hash)

  const scrollToSection = useCallback((sectionId: string) => {
    const element = document.getElementById(sectionId)
    if (element) {
      const elementPosition = element.getBoundingClientRect().top + window.pageYOffset
      window.scrollTo({
        top: elementPosition - 100,
        behavior: 'smooth'
      })
    }
  }, [])

  // Anchor links (e.g. /patients/:hash#patient-files from the order page) — sections
  // render synchronously with the suspense-loaded patient, so wait one frame for
  // layout and scroll to the target.
  useEffect(() => {
    if (!location.hash) return
    const frame = requestAnimationFrame(() => scrollToSection(location.hash.slice(1)))
    return () => cancelAnimationFrame(frame)
  }, [location.hash, scrollToSection])

  const handleSavePatient = async (body: UpdatePatientBody) => {
    if (isReadOnly()) return
    await updateMutation.mutateAsync(body)
    setShowSuccess(true)
  }

  const handleVerifyId = async () => {
    if (isReadOnly()) return
    if (!patient.userHash) {
      setVerifyIdFeedback({
        open: true,
        severity: 'error',
        message: 'User hash is missing for this patient'
      })
      return
    }

    try {
      const response = await verifyIdMutation.mutateAsync(patient.userHash)
      setVerifyIdFeedback({
        open: true,
        severity: 'success',
        message: response.message || 'ID verification workflow restarted successfully'
      })
    } catch {
      setVerifyIdFeedback({
        open: true,
        severity: 'error',
        message: 'Failed to restart ID verification'
      })
    }
  }

  const handleRunDuplicateCheck = () => {
    if (!canUpdatePatient()) return
    duplicateCheckMutation.mutate(hash)
  }

  if (!patient) {
    return (
      <Box sx={{ p: 4 }}>
        <Typography>Patient not found</Typography>
      </Box>
    )
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4, pb: 4 }}>
      {/* Back to Patient list */}
      <BackButton fallbackPath='/patients' defaultLabel='Back to Patient list' />

      {/* Success Message */}
      <Snackbar
        open={showSuccess}
        autoHideDuration={4000}
        onClose={() => setShowSuccess(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setShowSuccess(false)}
          severity='success'
          sx={{ width: '100%' }}
          icon={<CheckCircleOutlineIcon sx={{ color: '#2E7D32' }} />}
        >
          Patient details updated
        </Alert>
      </Snackbar>

      <Snackbar
        open={verifyIdFeedback.open}
        autoHideDuration={4000}
        onClose={() => setVerifyIdFeedback(current => ({ ...current, open: false }))}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setVerifyIdFeedback(current => ({ ...current, open: false }))}
          severity={verifyIdFeedback.severity}
          sx={{ width: '100%' }}
        >
          {verifyIdFeedback.message}
        </Alert>
      </Snackbar>

      {/* Header Section - Always visible with tabs */}
      <Header
        patient={patient}
        activeTab={activeTab}
        onTabChange={setActiveTab}
        onVerifyId={handleVerifyId}
        isVerifyingId={verifyIdMutation.isPending}
        onRunDuplicateCheck={handleRunDuplicateCheck}
        isRunningDuplicateCheck={isRunningDuplicateCheck}
        canRunDuplicateCheck={canUpdatePatient()}
        readOnly={readOnly}
      />

      {/* Timeline tab — full-width two-column layout */}
      {activeTab === 'timeline' ? (
        <PatientTimeline patientName={`${patient.firstName} ${patient.lastName}`} patientHash={hash} />
      ) : (
        /* Overview / IDU — standard layout with sidebar */
        <Grid container spacing={4}>
          {/* Main Content Area - 2/3 width */}
          <Grid
            size={9}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 7
            }}
          >
            <PatientInfo
              patient={patient}
              onSave={handleSavePatient}
              isSaving={updateMutation.isPending}
              readOnly={readOnly}
            />
            <LinkedPatientsSection patientHash={patient.hash} linkedPatientHash={patient.linkedPatientHash} />
            <MedicalHistory
              patient={patient}
              onSave={handleSavePatient}
              isSaving={updateMutation.isPending}
              readOnly={readOnly}
            />
            <PatientFilesSection patientHash={hash} orderNumbers={patientOrderNumbers} />
            <PreviousMedicationRequestsList
              medicationRequests={medicationRequests}
              isLoading={isMedReqsLoading}
              sectionId='medication-requests'
              title='Medication requests'
              onItemClick={medId =>
                navigate(`/med-requests/${medId}`, {
                  state: {
                    backLabel: 'Back to Patient',
                    fallbackPath: `/patients/${hash}`
                  }
                })
              }
            />
            <InvestigationsList
              investigations={investigations}
              isLoading={isInvestigationsLoading}
              sectionId='investigations'
              title='Investigations'
              onItemClick={investigationId =>
                navigate(`/investigations/${investigationId}`, {
                  state: {
                    backLabel: 'Back to Patient',
                    fallbackPath: `/patients/${hash}`
                  }
                })
              }
            />
            <SCRData />
            <SafeguardingQuestions patient={patient} />
          </Grid>

          {/* Right Sidebar - 1/3 width */}
          <Grid
            size={3}
            sx={{
              position: 'sticky',
              top: '88px',
              alignSelf: 'flex-start',
              display: 'flex',
              flexDirection: 'column',
              gap: 3
            }}
          >
            {/* Alert Cards */}
            {/* Not `patient.isDuplicate`: that legacy creation-time flag is never set or cleared by the pairs table. */}
            {pendingDuplicates && pendingDuplicates.merge + pendingDuplicates.link > 0 && (
              <PotentialDuplicateCard tab={pendingDuplicates.merge > 0 ? 'merge' : 'link'} />
            )}

            {/* Navigation Menu */}
            <NavMenu items={navItems} onNavigate={scrollToSection} />
          </Grid>
        </Grid>
      )}
    </Box>
  )
}
